import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Search, User, Phone, Mail, MapPin, Calendar, Loader2 } from "lucide-react";

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");

  const load = (q = "") => {
    setLoading(true);
    api.get("/admin/clients", { params: q ? { q } : {} })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (query) setParams({ q: query });
    else setParams({});
    load(query);
  };

  const totalClients = items.length;
  const totalCA = useMemo(
    () => items.reduce((s, c) => s + (c.total_spent || 0), 0),
    [items]
  );

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-10">
        <div>
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Gestion</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark">Clients</h1>
          <p className="text-sm text-fegray mt-2">
            {totalClients} client{totalClients > 1 ? "s" : ""} · {totalCA.toFixed(2)}€ de CA cumulé
          </p>
        </div>
      </div>

      <form onSubmit={onSearch} className="mb-6 max-w-md" data-testid="clients-search-form">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-fegray/50" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher par nom, téléphone, email…"
            data-testid="clients-search-input"
            className="w-full bg-white border border-gray-200 rounded-full pl-11 pr-4 py-3 text-sm focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
          />
        </div>
      </form>

      <div className="fe-card overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-fegray">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-fegray">
            {query ? `Aucun client trouvé pour "${query}"` : "Aucun client"}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-clients-table">
            <thead>
              <tr className="bg-fegray-light text-xs uppercase tracking-wider text-fegray border-b border-gray-100">
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Contact</th>
                <th className="text-left p-4">Ville</th>
                <th className="text-center p-4">Réservations</th>
                <th className="text-right p-4">Total dépensé</th>
                <th className="text-right p-4">Panier moyen</th>
                <th className="text-left p-4">Dernière résa</th>
                <th className="text-left p-4">Fiche</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const fullName = `${c.prenom || ""} ${c.nom || ""}`.trim();
                const initials = (c.prenom?.[0] || "") + (c.nom?.[0] || "");
                return (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-fegray-light/50 transition-colors" data-testid={`client-row-${c.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-full bg-fegreen text-white font-bold text-xs flex items-center justify-center shrink-0 uppercase">
                          {initials || "?"}
                        </span>
                        <div>
                          <div className="font-bold text-fegray-dark">{fullName || "Sans nom"}</div>
                          {c.sexe && (
                            <span className="text-[10px] text-fegray uppercase tracking-wider">{
                              c.sexe === "F" ? "Femme" : c.sexe === "M" ? "Homme" : "—"
                            }</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-fegray-dark">
                      <div className="text-xs">{c.email}</div>
                      <div className="text-xs text-fegray">{c.telephone}</div>
                    </td>
                    <td className="p-4 text-xs text-fegray-dark">
                      {c.ville || "—"}
                      {c.code_postal && <div className="text-fegray">{c.code_postal}</div>}
                    </td>
                    <td className="p-4 text-center">
                      <span className="font-bold text-fegray-dark">{c.nb_reservations || 0}</span>
                      {c.nb_paid > 0 && (
                        <div className="text-[10px] text-fegreen uppercase font-bold">{c.nb_paid} payées</div>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <span className="font-bold text-fegreen">{(c.total_spent || 0).toFixed(2)}€</span>
                    </td>
                    <td className="p-4 text-right text-fegray-dark">
                      {(c.avg_basket || 0).toFixed(2)}€
                    </td>
                    <td className="p-4 text-xs text-fegray">
                      {c.last_reservation_at ? new Date(c.last_reservation_at).toLocaleDateString("fr-FR") : "—"}
                    </td>
                    <td className="p-4">
                      <Link
                        to={`/admin/clients/${c.id}`}
                        data-testid={`client-card-link-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs font-bold text-fegreen hover:underline"
                      >
                        Voir la fiche →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
